/**
 * Asteroids Game
 * Destroy asteroids with your spaceship
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Asteroids = ({ onHome }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('asteroids_score', 0);
  const [highScore, setHighScore] = useLocalStorage('asteroids_highScore', 0);

  const gameStateRef = useRef({
    ship: { x: 200, y: 300, angle: 0, radius: 10 },
    bullets: [],
    asteroids: [],
  });

  const CANVAS_WIDTH = 400;
  const CANVAS_HEIGHT = 600;

  const startGame = () => {
    gameStateRef.current = {
      ship: { x: 200, y: 300, angle: 0, radius: 10 },
      bullets: [],
      asteroids: Array.from({ length: 3 }, (_, i) => ({
        x: Math.random() * CANVAS_WIDTH,
        y: Math.random() * 100 + 20,
        vx: (Math.random() - 0.5) * 3,
        vy: Math.random() * 2 + 1,
        radius: 30,
      })),
    };
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  const resetGame = () => {
    if (score > highScore) setHighScore(score);
    setGameState(GAME_CONFIG.states.MENU);
  };

  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameStateRef.current;

    const gameLoop = setInterval(() => {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Update bullets
      game.bullets = game.bullets.filter((b) => {
        b.x += Math.cos(b.angle) * 7;
        b.y += Math.sin(b.angle) * 7;
        return b.x > 0 && b.x < CANVAS_WIDTH && b.y > 0 && b.y < CANVAS_HEIGHT;
      });

      // Update asteroids
      game.asteroids.forEach((a) => {
        a.x += a.vx;
        a.y += a.vy;
        if (a.x < 0) a.x = CANVAS_WIDTH;
        if (a.x > CANVAS_WIDTH) a.x = 0;
        if (a.y > CANVAS_HEIGHT) a.y = 0;
      });

      // Collision detection
      game.bullets.forEach((b, bIdx) => {
        game.asteroids.forEach((a, aIdx) => {
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          if (Math.sqrt(dx * dx + dy * dy) < a.radius) {
            game.bullets.splice(bIdx, 1);
            game.asteroids.splice(aIdx, 1);
            setScore((prev) => prev + 100);
          }
        });
      });

      // Draw ship
      ctx.save();
      ctx.translate(game.ship.x, game.ship.y);
      ctx.rotate(game.ship.angle);
      ctx.fillStyle = GAME_CONFIG.colors.accent;
      ctx.beginPath();
      ctx.moveTo(10, 0);
      ctx.lineTo(-10, -10);
      ctx.lineTo(-5, 0);
      ctx.lineTo(-10, 10);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Draw bullets
      ctx.fillStyle = GAME_CONFIG.colors.accent2;
      game.bullets.forEach((b) => {
        ctx.fillRect(b.x - 2, b.y - 2, 4, 4);
      });

      // Draw asteroids
      ctx.strokeStyle = GAME_CONFIG.colors.primary;
      ctx.lineWidth = 2;
      game.asteroids.forEach((a) => {
        ctx.beginPath();
        ctx.arc(a.x, a.y, a.radius, 0, Math.PI * 2);
        ctx.stroke();
      });
    }, 30);

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') game.ship.angle -= 0.1;
      if (e.key === 'ArrowRight') game.ship.angle += 0.1;
      if (e.key === ' ') {
        e.preventDefault();
        game.bullets.push({
          x: game.ship.x + Math.cos(game.ship.angle) * 15,
          y: game.ship.y + Math.sin(game.ship.angle) * 15,
          angle: game.ship.angle,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      clearInterval(gameLoop);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameState, score, highScore]);

  return (
    <GameLayout
      title="Asteroids 💫"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.MENU && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Asteroids</h2>
            <p style={styles.menuDescription}>
              Destroy all asteroids!
            </p>
            <button onClick={startGame} className="btn btn-primary btn-lg" style={{ marginTop: '1rem' }}>
              🎮 Start Game
            </button>
          </div>
        )}

        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={styles.canvas} />
            <div style={styles.controls}>Arrow Keys Rotate | Space Shoot</div>
          </>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.gameOverCard}>
            <h1 style={styles.gameOverTitle}>Game Over</h1>
            <ScoreDisplay score={score} stats={{ 'Final Score': score }} />
            <button onClick={startGame} className="btn btn-primary mt-3" style={{ width: '100%' }}>
              🔄 Play Again
            </button>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
  },
  canvas: {
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: '4px',
    backgroundColor: '#000',
  },
  controls: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  gameOverCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '400px',
  },
  gameOverTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1rem',
    fontSize: '2rem',
  },
};

export default Asteroids;
